import { useQuery } from '@hooks/reactQuery';

import {
	getNetconfEventTypes,
	getNetconfMiddlewares,
	getNetconfNetworkElements,
	getNetconfVendors,
} from '@api/netconfEvents';

import { extractQueryParams } from '@utils/common';
import { nameTitleToOptionFormatted, optionsFormattedWithArray } from '@utils/formatter';

import { IAlarmFilter } from '@interfaces/alarms/alarm';

import { EUserRoles } from '@enums/common';

import {
	NETCONF_EVENT_EVENTS_TYPES,
	NETCONF_EVENT_MIDDLEWARES,
	NETCONF_EVENT_NETWORK_ELEMETNS,
	NETCONF_EVENT_VENDORS,
} from '@constants/queryKeys';

interface IUseQueryNetconfEventsDropdownValuesReturnType {
	netconfFilters: IAlarmFilter[];
	isLoadingNetconfEventsDropdownValues: boolean;
}

export const useQueryNetconfEventsDropdownValues = (): IUseQueryNetconfEventsDropdownValuesReturnType => {
	const { data: eventTypes = [], isLoading: isLoadingEventTypes } = useQuery({
		queryKey: [NETCONF_EVENT_EVENTS_TYPES],
		queryFn: extractQueryParams(getNetconfEventTypes),
		enabled: [true],
		select: (data) => optionsFormattedWithArray(data?.map((item: { name: string }) => item.name)),
		allowedRoles: [EUserRoles.smoLightUpdater, EUserRoles.smoLightViewer],
	});

	const { data: middlewares = [], isLoading: isLoadingMiddlewares } = useQuery({
		queryKey: [NETCONF_EVENT_MIDDLEWARES],
		queryFn: extractQueryParams(getNetconfMiddlewares),
		select: (data) => optionsFormattedWithArray(data?.map((item: { name: string }) => item.name)),
		enabled: [true],
		allowedRoles: [EUserRoles.smoLightUpdater, EUserRoles.smoLightViewer],
	});

	const { data: networkElements = [], isLoading: isLoadingNetworkElements } = useQuery({
		queryKey: [NETCONF_EVENT_NETWORK_ELEMETNS],
		queryFn: extractQueryParams(getNetconfNetworkElements),
		select: (data) => optionsFormattedWithArray(data?.map((item: { name: string }) => item.name)),
		enabled: [true],
		allowedRoles: [EUserRoles.smoLightUpdater, EUserRoles.smoLightViewer],
	});

	const { data: vendorOptions = [], isLoading: isLoadingVendorOptions } = useQuery({
		queryKey: [NETCONF_EVENT_VENDORS],
		queryFn: extractQueryParams(getNetconfVendors),
		select: (data) => nameTitleToOptionFormatted(data),
		enabled: [true],
		allowedRoles: [EUserRoles.smoLightUpdater, EUserRoles.smoLightViewer],
	});

	const netconfFilters = [
		{
			name: 'networkElementName',
			placeholder: 'Network Element',
			data: networkElements || [],
		},
		{
			name: 'vendor',
			placeholder: 'Vendor',
			data: vendorOptions || [],
		},
		{
			name: 'middlewareName',
			placeholder: 'Middleware',
			data: middlewares || [],
		},
		{
			name: 'eventType',
			placeholder: 'Event Type',
			data: eventTypes || [],
		},
	];

	return {
		netconfFilters,
		isLoadingNetconfEventsDropdownValues:
			isLoadingEventTypes ||
			isLoadingMiddlewares ||
			isLoadingNetworkElements ||
			isLoadingVendorOptions,
	};
};
